import { Progress, Tooltip } from "antd";
import type { FC } from "react";

interface HealthScorePillProps {
	healthScore: number | undefined;
	size?: "sm" | "md" | "lg";
	className?: string;
}

const HealthScorePill: FC<HealthScorePillProps> = ({
	healthScore,
	size = "md",
	className = "w-100",
}) => {
	const score = healthScore ?? 0;

	const getStrokeColor = (valueEnd: number) => {
		if (valueEnd >= 75) {
			return "#3f6e01"; // Green
		}
		if (valueEnd >= 25 && valueEnd < 75) {
			return "#e0a724"; // Yellow
		}
		return "#bf1e2d"; // Red
	};

	// Compact circle for table cells
	if (size === "sm") {
		const color = getStrokeColor(score);
		return (
			<Tooltip title={`Health Score: ${Math.round(score)}`}>
				<div
					style={{
						width: 32,
						height: 32,
						borderRadius: "50%",
						backgroundColor: color,
						color: "#fff",
						fontSize: 11,
						fontWeight: 700,
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "default",
					}}
				>
					{Math.round(score)}
				</div>
			</Tooltip>
		);
	}

	const bubbleSize = size === "md" ? 24 : 36;
	const fontSize = size === "md" ? 10 : 15;

	const getBubblePosition = (valueEnd: number) => {
		if (valueEnd === 0) {
			return "0px"; // Align flush to the start at 0
		}
		if (valueEnd >= 95) {
			return `calc(100% - ${bubbleSize}px)`; // Limit to the end for 95 and above
		}
		return `calc(${valueEnd}% - ${bubbleSize / 2}px)`; // Center the bubble
	};

	return (
		<div className="health-score-pill justify-content-lg-end align-content-lg-end justify-content-center align-items-center d-flex w-100 h-100">
			<div
				className={`health-score-container ${className}`}
				style={{ position: "relative", minWidth: "120px" }}
			>
				<div
					className={"health-dot"}
					style={{
						position: "absolute",
						left: getBubblePosition(score),
						width: `${bubbleSize}px`,
						height: `${bubbleSize}px`,
						fontSize: `${fontSize}px`,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						borderRadius: "50%",
						color: "#fff",
						backgroundColor: getStrokeColor(score),
					}}
				>
					{score}
				</div>
				<Progress
					percent={score}
					showInfo={false}
					strokeColor={getStrokeColor(score)}
					trailColor="#f0f0f0"
				/>
			</div>
		</div>
	);
};

export { HealthScorePill };
